import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail } from 'lucide-react';
import { toast } from 'sonner';
import { isNeverInvited, type User } from '@bliks-io/contracts';
import { Button, ConfirmDialog } from '@bliks-io/ui';
import type { ApiError } from '@/lib/agent.types';
import { translateErrorMessage } from '@/lib/errors';
import { toastRequestError } from '@/api/mutations/toast-api-error';
import { useBulkSendOrgMemberInvitations } from '@/api/mutations/useOrgMembers';

interface InviteAllInactiveButtonProps {
  members: User[];
}

/**
 * Header action that invites every never-invited ("inactive") member shown on
 * the page in one request. The list of ids is derived from the rows the page is
 * already rendering, so it invites exactly what the admin sees.
 */
export function InviteAllInactiveButton({ members }: InviteAllInactiveButtonProps) {
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const mutation = useBulkSendOrgMemberInvitations();

  const inactive = useMemo(() => members.filter((m) => isNeverInvited(m.status)), [members]);

  const handleConfirm = async () => {
    try {
      const { invitedUserIds, skipped } = (await mutation.mutateAsync(inactive.map((m) => m._id)))
        .data;
      if (invitedUserIds.length > 0) {
        toast.success(
          t('organization.members.inviteAllInactiveSuccess', {
            count: invitedUserIds.length
          })
        );
      }
      if (skipped.length > 0) {
        toast.warning(
          t('organization.members.inviteAllInactiveSkipped', {
            count: skipped.length
          })
        );
      }
      setConfirmOpen(false);
    } catch (error) {
      toastRequestError(error, translateErrorMessage(error as ApiError, t));
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="medium"
        disabled={inactive.length === 0}
        onClick={() => setConfirmOpen(true)}
      >
        <Mail className="w-4 h-4" />
        {t('organization.members.inviteAllInactive')}
      </Button>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        icon={<Mail className="w-5 h-5" />}
        title={t('organization.members.inviteAllInactive')}
        description={t('organization.members.inviteAllInactiveConfirm', {
          count: inactive.length
        })}
        confirmLabel={t('organization.members.inviteAllInactive')}
        onConfirm={handleConfirm}
        isPending={mutation.isPending}
      />
    </>
  );
}
